import { useAccount } from '@/hooks/useAccount';
import { AccountLoadError, getAccountAccessToken } from '@/lib/account-session';
import { describeCard } from '@/lib/billing';
import { PqClient, PqServiceStatus, type ProblemDetails } from '@/lib/pq-client';
import {
    clearPendingConfirmation, describeReactivationFailure, formatAmount, formatDate, formatTime, isQuoteExpired,
    loadPendingConfirmation, parseAttempt, parseQuote, savePendingConfirmation,
    type PendingConfirmation, type ReactivationFailure, type ReactivationQuote,
} from '@/lib/reactivation';
import { Alert, Anchor, Button, Card, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCircleCheck } from '@tabler/icons-react';
import { useAuth } from '@workos-inc/authkit-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

type Phase =
    | { name: 'idle' }
    | { name: 'loading' }
    | { name: 'review'; quote: ReactivationQuote }
    | { name: 'confirming'; quote: ReactivationQuote | null; pending: PendingConfirmation }
    /** A confirmation was sent but its outcome is unknown; recover with the same identifiers. */
    | { name: 'uncertain'; pending: PendingConfirmation; message: string }
    | { name: 'completed'; transactionReference: string | null; entering: boolean; notActiveYet: boolean }
    | { name: 'stopped'; failure: ReactivationFailure };

const BASE = '/account/me/reactivation';
/** Seconds the payment confirmation stays on screen before entering the app. */
const CONTINUE_DELAY = 5;

interface ReactivationProps {
    /** Card and address edits must wait while a quote is under review or a payment is unresolved. */
    onBusyChange: (busy: boolean) => void;
}

/**
 * Explicit pay-and-reactivate for an expired monthly Mobile subscription: review a server quote,
 * then pay only on the customer's click. Confirmations are idempotent; after an uncertain result the
 * same quote ID and Idempotency-Key are reused, never a new key.
 */
const Reactivation: React.FC<ReactivationProps> = ({ onBusyChange }) => {
    const { getAccessToken, signIn } = useAuth();
    const { userId, refreshAccount } = useAccount();
    const navigate = useNavigate();
    const [phase, setPhase] = useState<Phase>({ name: 'idle' });
    const [now, setNow] = useState(() => Date.now());
    const [countdown, setCountdown] = useState(CONTINUE_DELAY);
    const inFlight = useRef(false);
    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    useEffect(() => {
        onBusyChange(['loading', 'review', 'confirming', 'uncertain'].includes(phase.name));
    }, [onBusyChange, phase.name]);

    // Re-render while reviewing so an expired quote can no longer be paid.
    useEffect(() => {
        if (phase.name !== 'review') return;
        const timer = window.setInterval(() => setNow(Date.now()), 15_000);
        return () => window.clearInterval(timer);
    }, [phase.name]);

    const request = useCallback(async (method: 'GET' | 'POST', path: string, idempotencyKey?: string) => {
        const token = await getAccountAccessToken(getAccessToken);
        return new PqClient().request<unknown>(token, method, path, {
            returnResponse: true,
            headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
        });
    }, [getAccessToken]);

    const stop = (stage: 'quote' | 'confirm', status: number, problem: ProblemDetails | null) =>
        setPhase({ name: 'stopped', failure: describeReactivationFailure(stage, status, problem) });

    const complete = useCallback((transactionReference: string | null) => {
        clearPendingConfirmation(userId);
        // The account isn't refreshed yet, so this page stays put while the customer reads the result.
        setPhase({ name: 'completed', transactionReference, entering: false, notActiveYet: false });
        setCountdown(CONTINUE_DELAY);
        // The reference also stays available as a notification after the app opens.
        notifications.show({
            color: 'green',
            title: 'Subscription reactivated',
            message: transactionReference ? `Payment successful. Reference: ${transactionReference}` : 'Payment successful.',
            autoClose: false,
        });
    }, [userId]);

    /** Fetches /mobile/me again and enters the app only once it reports Active. */
    const enterApp = useCallback(async () => {
        setPhase((current) => current.name === 'completed' ? { ...current, entering: true, notActiveYet: false } : current);
        try {
            const account = await refreshAccount();
            if (account.serviceStatus === PqServiceStatus.Active) {
                navigate('/', { replace: true });
                return;
            }
        } catch { /* fall through to the retry message */ }
        if (mounted.current) {
            setPhase((current) => current.name === 'completed' ? { ...current, entering: false, notActiveYet: true } : current);
        }
    }, [navigate, refreshAccount]);

    // After a successful payment, count down on screen, then continue to the app.
    const countingDown = phase.name === 'completed' && !phase.entering && !phase.notActiveYet;
    useEffect(() => {
        if (!countingDown) return;
        if (countdown <= 0) {
            void enterApp();
            return;
        }
        const timer = window.setTimeout(() => setCountdown((value) => value - 1), 1000);
        return () => window.clearTimeout(timer);
    }, [countingDown, countdown, enterApp]);

    /** Looks up a submitted confirmation; never charges. */
    const checkStatus = useCallback(async (pending: PendingConfirmation) => {
        if (inFlight.current) return;
        inFlight.current = true;
        setPhase({ name: 'confirming', quote: null, pending });
        try {
            const response = await request('GET', `${BASE}/${encodeURIComponent(pending.quoteId)}`);
            if (!mounted.current) return;
            const attempt = response.ok ? parseAttempt(response.data, pending.quoteId) : null;
            if (attempt?.status === 'Completed') return complete(attempt.transactionReference);
            if (attempt?.status === 'Failed') {
                clearPendingConfirmation(userId);
                return setPhase({ name: 'stopped', failure: { message: `${attempt.message ?? 'The payment did not go through.'} Review a new quote to try again.`, next: 'new-quote' } });
            }
            if (!response.ok && (response.status === 401 || response.status === 403)) return stop('confirm', response.status, response.problem);
            setPhase({
                name: 'uncertain', pending,
                message: attempt?.status === 'Unconfirmed'
                    ? 'Your payment was not submitted. You can finish it now.'
                    : 'Your payment is still being processed, or its result is not available yet.',
            });
        } catch (error) {
            if (!mounted.current) return;
            if (error instanceof AccountLoadError && error.requiresSignIn) return stop('confirm', 401, null);
            setPhase({ name: 'uncertain', pending, message: "We couldn't check the payment status. Check your connection and try again." });
        } finally {
            inFlight.current = false;
        }
    }, [complete, request, userId]);

    // Resume a confirmation left unresolved by a reload, crash or lost connection.
    useEffect(() => {
        const pending = loadPendingConfirmation(userId);
        if (pending) void checkStatus(pending);
    }, [checkStatus, userId]);

    const getQuote = async () => {
        if (inFlight.current) return;
        inFlight.current = true;
        setPhase({ name: 'loading' });
        try {
            const response = await request('POST', `${BASE}/quote`);
            if (!mounted.current) return;
            if (!response.ok) return stop('quote', response.status, response.problem);
            const quote = parseQuote(response.data);
            if (!quote) return setPhase({ name: 'stopped', failure: { message: 'Renewal details are unavailable. Please contact ParcelQuest support.', next: 'support' } });
            if ('supportRequired' in quote) return setPhase({ name: 'stopped', failure: { message: quote.message ?? "Your subscription can't be renewed in the app. Please contact ParcelQuest support.", next: 'support' } });
            setNow(Date.now());
            setPhase({ name: 'review', quote });
        } catch (error) {
            if (!mounted.current) return;
            stop('quote', error instanceof AccountLoadError && error.requiresSignIn ? 401 : 0, null);
        } finally {
            inFlight.current = false;
        }
    };

    /** Sends (or re-sends, with the same key) the explicit payment confirmation. */
    const confirm = async (pending: PendingConfirmation, quote: ReactivationQuote | null) => {
        if (inFlight.current) return;
        inFlight.current = true;
        // Saved before sending, so a reload mid-request can still recover this exact attempt.
        savePendingConfirmation(userId, pending);
        setPhase({ name: 'confirming', quote, pending });
        try {
            const response = await request('POST', `${BASE}/${encodeURIComponent(pending.quoteId)}/confirm`, pending.idempotencyKey);
            if (!mounted.current) return;
            if (response.ok) {
                const attempt = parseAttempt(response.data, pending.quoteId);
                if (attempt?.status === 'Completed') return complete(attempt.transactionReference);
                return setPhase({ name: 'uncertain', pending, message: 'Your payment is being processed. Check the status in a moment.' });
            }
            const failure = describeReactivationFailure('confirm', response.status, response.problem);
            if (failure.next === 'check-status') return setPhase({ name: 'uncertain', pending, message: failure.message });
            // A definite answer (declined, expired, support): nothing left to recover.
            clearPendingConfirmation(userId);
            setPhase({ name: 'stopped', failure });
        } catch (error) {
            if (!mounted.current) return;
            if (error instanceof AccountLoadError && error.requiresSignIn) {
                return setPhase({ name: 'uncertain', pending, message: 'Sign in again, then check your payment status.' });
            }
            setPhase({ name: 'uncertain', pending, message: describeReactivationFailure('confirm', 0, null).message });
        } finally {
            inFlight.current = false;
        }
    };

    const support = (
        <Text size="sm">
            Contact support: <Anchor href="mailto:mobilesupport@parcelquest.com">mobilesupport@parcelquest.com</Anchor> or{' '}
            <Anchor href="tel:+18337681224">(833) 768-1224</Anchor>.
        </Text>
    );

    let body: React.ReactNode;
    switch (phase.name) {
        case 'idle':
        case 'loading':
            body = (
                <>
                    <Text size="sm">Your subscription has expired. Review the renewal amount before paying; nothing is charged until you confirm.</Text>
                    <Button color="pq-red" loading={phase.name === 'loading'} onClick={() => void getQuote()}>Review renewal</Button>
                </>
            );
            break;
        case 'review':
        case 'confirming': {
            const quote = phase.quote;
            if (!quote) {
                body = <Button loading variant="default">Checking payment status</Button>;
                break;
            }
            const expired = phase.name === 'review' && isQuoteExpired(quote, now);
            const price = formatAmount(quote.amount, quote.currency);
            body = (
                <>
                    <Card withBorder padding="sm">
                        <Stack gap={4}>
                            <Text size="sm"><strong>Amount:</strong> {price}</Text>
                            <Text size="sm"><strong>Card:</strong> {quote.card ? describeCard(quote.card) : 'Card on file'}</Text>
                            <Text size="sm"><strong>Active through:</strong> {formatDate(quote.newEndDate)}</Text>
                            <Text size="xs" c="dimmed">Quote valid until {formatTime(quote.expiresAt)}</Text>
                        </Stack>
                    </Card>
                    {expired ? (
                        <>
                            <Text size="sm" c="red">This quote has expired.</Text>
                            <Button color="pq-red" onClick={() => void getQuote()}>Review a new quote</Button>
                        </>
                    ) : (
                        <Button
                            color="pq-red"
                            size="lg"
                            loading={phase.name === 'confirming'}
                            onClick={() => void confirm({ quoteId: quote.quoteId, idempotencyKey: crypto.randomUUID() }, quote)}
                        >
                            Pay {price} and reactivate
                        </Button>
                    )}
                    <Button variant="subtle" color="gray" disabled={phase.name === 'confirming'} onClick={() => setPhase({ name: 'idle' })}>Cancel</Button>
                </>
            );
            break;
        }
        case 'uncertain':
            body = (
                <>
                    <Alert color="yellow">{phase.message}</Alert>
                    <Button color="pq-red" onClick={() => void checkStatus(phase.pending)}>Check payment status</Button>
                    {/* Same quote and Idempotency-Key: the server returns the original result instead of charging again. */}
                    <Button variant="default" onClick={() => void confirm(phase.pending, null)}>Retry payment</Button>
                    <Text size="xs" c="dimmed">Retrying uses the same payment request, so you won't be charged twice.</Text>
                    {support}
                </>
            );
            break;
        case 'completed':
            body = (
                <>
                    <Alert color="green" icon={<IconCircleCheck />} title="Subscription reactivated">
                        Your payment was successful.{phase.transactionReference && <> Reference: <strong>{phase.transactionReference}</strong></>}
                    </Alert>
                    {phase.notActiveYet ? (
                        <Text size="sm" role="status">Your account is still being updated. Try again in a moment.</Text>
                    ) : (
                        <Text size="sm" c="dimmed" role="status" aria-live="polite">
                            {phase.entering ? 'Opening the map…' : `Taking you to the map in ${countdown}…`}
                        </Text>
                    )}
                    <Button color="pq-red" loading={phase.entering} onClick={() => void enterApp()}>Continue to the map</Button>
                </>
            );
            break;
        case 'stopped':
            body = (
                <>
                    <Alert color="red" role="alert">{phase.failure.message}</Alert>
                    {phase.failure.next === 'new-quote' && <Button color="pq-red" onClick={() => void getQuote()}>Review a new quote</Button>}
                    {phase.failure.next === 'sign-in' && <Button color="pq-red" onClick={() => void signIn().catch(() => undefined)}>Sign in again</Button>}
                    {phase.failure.next === 'support' && support}
                </>
            );
            break;
    }

    return (
        <Stack gap="xs">
            <Title order={5}>Renew subscription</Title>
            {body}
        </Stack>
    );
};

export default Reactivation;
